import React, { useEffect, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { profileService } from '../../../services';
import { EmailPreferenceType } from '../../../types/misc';
import useAuth from '../../../hooks/useAuth';
import CommonPageWrapper from '../../../components/layouts/CommonPageWrapper';
import { redirectToApp } from '../../../utils/appUtils';
import ResponsePageWrapper from '../../../components/layouts/ResponsePageWrapper';
import { InviteType } from '../../../types/invites';
import InvalidAuthBody from '../../../components/misc/InvalidAuthBody';
import Button from '../../../components/misc/Button';
import useMagicLogin from '../../../hooks/useMagicLogin';
import useDeleteParams from '../../../hooks/useDeleteParams';

export default function EmailPreferencesPage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const link = params.get('link');

  if (useMagicLogin()) {
    return <></>;
  }

  useDeleteParams('link');

  const type = params.get('type') as EmailPreferenceType | undefined;

  useEffect(() => {
    if (!type || !Object.values(EmailPreferenceType).includes(type)) {
      navigate('/');
      toast.error(t('toast.error.general.invalidInvite'));
    }
  }, [JSON.stringify(auth)]);

  useLayoutEffect((): void => {
    redirectToApp(link, null, 'preferences', null);
  }, []);

  const handleSubmit = async () => {
    const current = await profileService.getUserPreferences();
    const wanted = (type as string).trim().toUpperCase();
    const alreadyOptedOut = current.some(
      (preference) => typeof preference === 'string' && preference.trim().toUpperCase() === wanted,
    );
    const next = alreadyOptedOut ? current : [...current, type!];
    await profileService.updateUserPreferences(next);
    navigate('/');
    toast.success('succes');
  };

  const typeToString = (): string => {
    switch (type) {
      case EmailPreferenceType.COMMUNITYEMAIL:
        return t('page.emailPreferences.group');
      case EmailPreferenceType.DATAREQUESTEMAIL:
        return t('page.emailPreferences.dataRequest');
      case EmailPreferenceType.INTRODUCTIONEMAIL:
        return t('page.emailPreferences.introduction');
      case EmailPreferenceType.NEWCONNECTIONEMAIL:
        return t('page.emailPreferences.connection');
      case EmailPreferenceType.MARKETING_AUTOMATION:
        return t('page.emailPreferences.marketingAutomation');
      default:
        return '';
    }
  };

  if (!auth)
    return (
      <ResponsePageWrapper>
        <InvalidAuthBody invitationId={type as string} inviteType={InviteType.EMAIL_PREFERENCE} />
      </ResponsePageWrapper>
    );

  return (
    <CommonPageWrapper>
      <CommonPageWrapper.Title>{t('page.emailPreferences.title')}</CommonPageWrapper.Title>

      <CommonPageWrapper.Body>
        <div className="mb-8 flex flex-col border-b border-secondary-50 pb-8">
          <p className="mt-2 font-[600px]">{t('page.emailPreferences.description')}</p>
          <Button className="mt-4 w-fit px-[32px]" variant="primary" onClick={handleSubmit}>
            {t('page.emailPreferences.button', { type: typeToString() })}
          </Button>
        </div>
      </CommonPageWrapper.Body>
    </CommonPageWrapper>
  );
}
